import {
  currencyFactorForFormattedAmount,
  formattedPriceToUnitAmount,
  getConfigForCurrency,
  roundAmount
} from "components/FormerEditor/common/currency";
import { formatUTCTime } from "utils/date";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import get from "lodash/get";
import SERVICES from "utils/constants/services";
import { PRODUCT, PRICE } from "utils/constants/models";
import {
  INTEGRATION_ID,
  INTEGRATION_MODEL_ADMIN_URL,
  INTEGRATION_MODEL,
  INTEGRATION_MODEL_UID,
  INTEGRATION_PARENT_MODEL,
  INTEGRATION_PARENT_MODEL_UID,
  INTEGRATION_SERVICE,
  INTEGRATION_SERVICE_UID
} from "utils/constants/metadata";
import { squarespaceProductAdminUrl } from "utils/constants/integration/squarespace";
import { matchingImport } from ".";

const PHYSICAL = "PHYSICAL";
const SQUARESPACE_PRODUCT_TYPE = {
  PHYSICAL
};

/**
 * Before there is support for setting variants via the UI
 * - default to something we know is unique across prices
 * - squarespace products dont have flexibility of multiple prices
 * - they have base price and sale price
 */
const SKU = "sku";
const ATTRIBUTES = {
  SKU
};

const SQUARESPACE_ROOT = ".squarespace.com";

export const getSquarespaceShop = (shop) => `${shop}${SQUARESPACE_ROOT}`;

const getServiceModelMetadata = ({
  integration,
  modelUID,
  service,
  model
}) => ({
  [INTEGRATION_ID]: integration.id,
  [INTEGRATION_SERVICE]: service,
  /** Ideally this is a unique id for the shop */
  [INTEGRATION_SERVICE_UID]: integration.service_uid,
  [INTEGRATION_MODEL_UID]: modelUID,
  [INTEGRATION_MODEL]: model,
  [INTEGRATION_MODEL_ADMIN_URL]: squarespaceProductAdminUrl(integration.domain)
});
const BASE_PRICE = "basePrice";
const SALE_PRICE = "salePrice";
const SQUARESPACE_PRICE_KEYS = [BASE_PRICE, SALE_PRICE];

export const prepareSquarespaceProduct = ({
  product,
  integration,
  merchantProducts
}) => {
  const importMatch = matchingImport({
    merchantProducts,
    product,
    integration
  });
  const adminUrl = squarespaceProductAdminUrl(integration.domain);

  const prices = product.variants.reduce((memo, variant) => {
    const common = {
      inventory: variant.stock.quantity || 0,
      nickname: variant.sku,
      recurring: null,
      metadata: {
        [INTEGRATION_PARENT_MODEL_UID]: product.id,
        [INTEGRATION_PARENT_MODEL]: PRODUCT,
        ...getServiceModelMetadata({
          integration,
          // Shopify variant (price) id
          modelUID: variant.id,
          service: SERVICES.SQUARESPACE,
          model: PRICE
        })
      }
    };
    SQUARESPACE_PRICE_KEYS.forEach((priceKey) => {
      const priceData = variant.pricing[priceKey];
      /**
       * Only process sale price is onSale is true
       * Else ensure that price data exists to process standard prices
       */
      const process =
        priceKey === SALE_PRICE ? variant.pricing.onSale : priceData;
      if (process) {
        const currency = priceData.currency.toLowerCase();
        const currencyConfig = getConfigForCurrency(currency);

        const formattedPrice = `${currencyConfig.symbol}${priceData.value}`;

        memo.push({
          ...common,
          amount: formattedPriceToUnitAmount({ formattedPrice, currency }),
          formattedPrice,
          currency
        });
      }
    });
    return memo;
  }, []);

  /**
   * Show the range of prices of there are more than one
   * Single: $10
   * Multiple: $10 - $40
   */
  let priceRange;
  if (prices.length > 1) {
    const orderedPrices = uniqBy(orderBy(prices, "amount"), "amount");
    if (orderedPrices.length > 1) {
      priceRange = [
        orderedPrices[0].formattedPrice,
        orderedPrices.pop().formattedPrice
      ].join(" - ");
    } else {
      priceRange = orderedPrices[0].formattedPrice;
    }
  } else {
    priceRange = prices[0].formattedPrice;
  }
  const images = product.images.map(({ url }) => url.split("?")[0]);

  const result = {
    id: product.id,
    vendor: integration.domain,
    images,
    image: images[0],
    name: product.name,
    service: SERVICES.SQUARESPACE,
    metadata: getServiceModelMetadata({
      integration,
      // Shopify product id
      modelUID: product.id,
      service: SERVICES.SQUARESPACE,
      model: PRODUCT
    }),
    createdAt: formatUTCTime(product.createdOn),
    adminUrl,
    priceRange,
    prices,
    imported: Boolean(importMatch)
  };

  return result;
};

export const validateExportToSquarespaceProduct = ({ integration, values }) => {
  return {
    merchantAccount: values.merchantAccount,
    product: {
      ...values.product,
      variants: filterCompatibleProductPrices(integration, values.product)
    }
  };
};

export const filterIncompatibleProductPrices = (integration, product) => {
  const storeCurrency = get(integration, "raw.currency");
  return product.variants.filter(
    (variant) =>
      variant.pricing.basePrice.currency.toLowerCase() !==
      storeCurrency.toLowerCase()
  );
};

export const filterCompatibleProductPrices = (integration, product) => {
  const storeCurrency = get(integration, "raw.currency");
  return product.variants.filter(
    (variant) =>
      variant.pricing.basePrice.currency.toLowerCase() ===
      storeCurrency.toLowerCase()
  );
};

/**
 * Create product in squarespace and then sync back to the merchant account price
 */
export const prepareExportToSquarespaceProduct = ({
  merchantAccountUUID,
  product: { id, name, description, prices },
  storePages
}) => {
  const newProduct = {
    type: SQUARESPACE_PRODUCT_TYPE.PHYSICAL,
    name,
    storePageId: storePages.length ? storePages[0].id : "",
    variantAttributes: [ATTRIBUTES.SKU],
    variants: prices.reduce((memo, price) => {
      if (!price.recurring) {
        const factor = currencyFactorForFormattedAmount(price.currency);
        const amountToFormat = price.unit_amount * factor;
        const result = {
          sku: price.id,
          pricing: {
            basePrice: {
              currency: price.currency.toUpperCase(),
              value: `${roundAmount(amountToFormat)}`
            }
          },
          attributes: {
            [ATTRIBUTES.SKU]: price.id
          }
        };
        memo.push(result);
      }

      return memo;
    }, [])
  };
  if (description) {
    newProduct.description = `<p>${description}</p>`;
  }

  return {
    product: newProduct,
    merchantAccount: {
      uuid: merchantAccountUUID,
      model: PRODUCT,
      model_uid: id,
      service: SERVICES.STRIPE
    }
  };
};

export const prepareSquarespaceIntegration = ({ key, shop }) => ({
  key,
  shop: getSquarespaceShop(shop)
});

export const prepareStorePageOptions = (storePages = []) =>
  storePages.map &&
  storePages.map(({ id, title }) => ({
    uid: id,
    label: title
  }));

export const getUniqVariantCurrencies = (prices) =>
  prices.reduce((memo, { pricing: { basePrice: { currency } } }) => {
    if (memo.indexOf(currency) === -1) {
      memo.push(currency);
    }
    return memo;
  }, []);
